/**
 * Tipos compartilhados das telas admin.
 *
 * Esses tipos serão a "fonte de verdade" pra contratos com o backend.
 * Quando a Frente A (auth real) entregar, esses tipos precisam estar
 * alinhados com o schema do SQLite + endpoints da API.
 *
 * Convenção: IDs são strings (UUIDs no backend), datas são ISO strings.
 */

/**
 * Slugs dos departamentos pré-definidos da plataforma.
 *
 * Admin pode criar departamentos custom com slugs arbitrários — por isso
 * `Department.slug` é `string` genérico. Mas pra referências internas
 * (DepartmentRole.departmentSlug, mapeamento de menu, etc.) usamos esse
 * union pra ter autocomplete e validação dos slugs base.
 */
export type DepartmentSlug =
  | 'clientes'
  | 'comercial'
  | 'manutencao'
  | 'projetos'
  | 'financeiro'
  | 'rh'
  | 'compras';

/**
 * FEAT-V2-037 Fase A (Cowork 2026-05-25): slug do perfil de uso.
 *
 * IMPORTANTE: o slug 'comercial' COEXISTE com `DepartmentSlug='comercial'`
 * — são tabelas diferentes (`profiles` vs `departments`) e contextos
 * diferentes (perfil = O QUE você faz; departamento = ONDE você trabalha).
 * Tipos DIFERENTES no TS — código que mistura `ProfileSlug` com
 * `DepartmentSlug` é erro de compilação (mesmo runtime string, semantics
 * diferentes). Sempre passar o tipo certo pra função correta.
 */
export type ProfileSlug = 'comercial' | 'administrativo' | 'tecnico' | 'compras';

/**
 * Perfis seedados na Migration 060 + Migration 259 (Round D — perfil 'compras').
 * ⚠️ O perfil 'terceiro' (mig 130) MORREU na 1.3 do PRD-CICLO-DO-PROJETO
 * (14/09/2026, decisão 9 do PRD): o Portal do Terceiro foi apagado inteiro.
 * Compras = setor de Compras (gerencia fornecedores/cotação/faturamento direto).
 */
export const PROFILE_SLUGS: readonly ProfileSlug[] = [
  'comercial',
  'administrativo',
  'tecnico',
  'compras',
] as const;

/**
 * Mapeamento slug → nome display (PT-BR). Source-of-truth no DB
 * (`profiles.nome`) — este fallback cobre estados early-loading / offline
 * onde `useProfiles()` ainda não populou cache.
 */
export const PROFILE_SLUG_LABELS: Record<ProfileSlug, string> = {
  comercial: 'Comercial',
  administrativo: 'Administrativo',
  tecnico: 'Técnico',
  compras: 'Compras',
};

/**
 * Permission keys cobertas por override de perfil. Mesma lista do backend
 * (`server/lib/profile-defaults.ts` → `PROFILE_PERMISSION_KEYS`).
 */
export type ProfilePermissionKey =
  | 'pode_ver_tarefas_outros'
  | 'pode_inativar_cards'
  | 'pode_criar_tarefa_pra_outros';

export interface Profile {
  id: string;
  slug: ProfileSlug;
  nome: string;
  descricao: string | null;
  ordem: number;
  /** JSON parseado do `permission_overrides_json`. */
  permissionOverrides: Partial<Record<ProfilePermissionKey, 0 | 1>>;
  createdAt: string;
  updatedAt: string;
}

/** Shape `permissions` resolvidas que vem de /me + /users (FEAT-V2-037). */
export type ResolvedPermissions = Record<ProfilePermissionKey, 0 | 1>;

export type DashboardSlug =
  | 'comercial'
  | 'manutencao'
  | 'compras'
  | 'projetos'
  | 'financeiro'
  | 'gestao';

/**
 * Rótulos humanos dos dashboards/módulos (P4b AJUSTES-POS-2). Usar SEMPRE em
 * vez do slug técnico na UI (nada de "manutencao"/"/menu/comercial" vazando).
 */
export const DASHBOARD_SLUG_LABELS: Record<DashboardSlug, string> = {
  comercial: 'Comercial',
  manutencao: 'Manutenção',
  compras: 'Compras',
  projetos: 'Projetos',
  financeiro: 'Financeiro',
  gestao: 'Gestão',
};

export type RoleType = 'lider' | 'colaborador' | 'visualizador';

export interface Department {
  id: string;
  /** Slug do departamento — string livre pra permitir departamentos custom criados pelo admin. */
  slug: string;
  nome: string;
  descricao: string;
  ordem: number;
  ativo: boolean;
  /** Quantidade de usuários vinculados (computado) */
  userCount?: number;
}

export interface DepartmentRole {
  /** Slug do departamento — string livre porque admin pode criar departamentos custom. Validação que o slug exista é feita no UI/business layer (lista de departments ativos). */
  departmentSlug: string;
  role: RoleType;
}

export interface User {
  id: string;
  nome: string;
  email: string;
  isAdmin: boolean;
  ativo: boolean;
  ultimoAcessoIso: string | null;
  criadoEmIso: string;
  /**
   * URL da foto do usuário (ex: `https://lh3.googleusercontent.com/a/...` do Google).
   * Null quando o user ainda não fez login com Google ou não tem foto pública.
   * Atualizado automaticamente em todo login bem-sucedido.
   */
  fotoUrl?: string | null;
  /** Papéis em cada departamento que o usuário pertence */
  departamentos: DepartmentRole[];
  /**
   * ⚠️ NÃO PERSISTE. O `PUT /api/v1/users/:id` descarta esta chave (o Zod do
   * backend não a conhece) e `indicadores` só existia em mock. A tela de
   * Permissões deixou de editar isso na rodada PERMISSOES-ACESSO — era UI
   * mentindo pro admin. O campo continua aqui só porque o tipo é lido de
   * respostas antigas; acesso a tela agora é `acesso_tela`/`acesso_regra`
   * (migration 419, ver `src/lib/acesso-store.ts`). Se um dia o controle por
   * dashboard/indicador for pra valer, ele nasce como chave no catálogo.
   */
  permissoes?: {
    operacionais?: string[]; // ex: 'comercial.create_cards'
    dashboards?: DashboardSlug[];
    indicadores?: string[]; // ex: 'comercial.taxa_conversao'
  };
  /** UI-2 / FEAT-V2-022: permissão pra criar tarefa pra outros colaboradores. */
  podeCriarTarefaPraOutros?: boolean;
  /** UI-2 / FEAT-V2-024: permissão pra agendar tarefa sem aprovação. */
  podeAgendarSemAprovacao?: boolean;
  /** UI-2 / FEAT-V2-024: user que aprova tarefas deste user quando !podeAgendarSemAprovacao. */
  aprovadorId?: string | null;
  /** FEAT-V2-035: permite inativar/reativar cards. Admin sempre true (Migration 051). */
  podeInativarCards?: boolean;
  /**
   * FEAT-V2-036 (Migration 059, 2026-05-25): permissão pra ver tarefas
   * atribuídas a outros usuários. Default = true (vê tudo, comportamento
   * legacy). Quando false, `GET /api/v1/tasks` + `GET /api/v1/cards/:id/tasks`
   * filtram `WHERE responsavel_id = req.user.id` pra esse user (admin
   * sempre override).
   *
   * Sprint 2 (FEAT-V2-037 Fase A) vai override por perfil: Comercial e
   * Técnico → false, Administrativo → true.
   */
  podeVerTarefasOutros?: boolean;
  /**
   * FEAT-V2-037 Fase A (Migration 061, 2026-05-25): ID do perfil de uso.
   * NULL = super-admin único (matheus.mussi). Demais users SEMPRE têm
   * profileId não-null.
   */
  profileId?: string | null;
  /** FEAT-V2-037: slug do perfil (JOIN profiles.slug). NULL = super-admin. */
  profileSlug?: ProfileSlug | null;
  /** FEAT-V2-037: permissões resolvidas (super-admin → tudo 1; demais → user columns). */
  permissions?: ResolvedPermissions;
  /** FEAT-V2-037: shortcut `isAdmin && profileId === null`. */
  isSuperAdmin?: boolean;
  /**
   * INDICADORES-REDESIGN (Migration 382): vendedor "de verdade" — entra no
   * ranking, nas metas por-vendedor e no rateio. Quem tem o dept comercial
   * mas não é vendedor (orçamentista, apoio) segue com acesso normal ao
   * módulo, só não conta nesses números. Default false.
   */
  isVendedor?: boolean;
  /**
   * CA4 (Migration 406): segmentos de cliente que o usuário acessa no Cofre de
   * acessos e senhas. Lista vazia = acesso a TODOS os segmentos (padrão
   * preservado). Cada valor é um segmento (tag) de cliente.
   */
  cofreSegmentos?: string[];
  /**
   * CARD-VENDAS-VENDEDOR (Migration 491): código do vendedor do Omie a que
   * esta pessoa corresponde. Com o vínculo, a Home dela mostra o card
   * "Minhas vendas" do mês. `null` = sem vínculo (o card não aparece).
   */
  omieVendedorCodigo?: string | null;
  /**
   * 🔴 #269 (mig 590): o WhatsApp desta pessoa, e o uso é **um só** — o bot
   * IGNORA as mensagens dela nos grupos. ❌ Não é notificação: aquilo é o
   * booleano `user_notification_preferences.whatsapp`, e confundir os dois é
   * exatamente o que o handoff mandou evitar. ❌ Não é obrigatório.
   */
  telefoneWhatsapp?: string | null;
}

export interface DashboardIndicator {
  slug: string; // ex: 'comercial.taxa_conversao'
  label: string;
  description: string;
  dashboard: DashboardSlug;
}

/**
 * Permissões granulares operacionais por departamento.
 * Cada departamento tem o mesmo conjunto de permissões disponíveis.
 */
export const OPERATIONAL_PERMISSIONS = [
  { key: 'view', label: 'Visualizar', description: 'Pode ver as telas do departamento' },
  { key: 'create_cards', label: 'Criar cards', description: 'Pode criar cards em workflows' },
  { key: 'move_cards', label: 'Mover cards', description: 'Pode mover cards entre colunas' },
  { key: 'edit_workflow', label: 'Editar workflows', description: 'Pode criar/editar workflows e colunas' },
  { key: 'manage_automations', label: 'Gerenciar automações', description: 'Pode configurar automações' },
  { key: 'view_reports', label: 'Ver relatórios', description: 'Pode acessar relatórios do departamento' },
  { key: 'manage_team', label: 'Gerenciar equipe', description: 'Pode adicionar/remover membros' },
] as const;

export const ROLES_LABELS: Record<RoleType, { label: string; description: string }> = {
  lider: {
    label: 'Líder',
    description: 'Acesso completo ao departamento + edição de workflows e automações',
  },
  colaborador: {
    label: 'Colaborador',
    description: 'Pode operar (criar e mover cards), sem editar estrutura',
  },
  visualizador: {
    label: 'Visualizador',
    description: 'Apenas leitura — não pode criar nem editar',
  },
};
